import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { toast, swalConfirm } from '@/lib/swal';
import {
  emailTemplateLibraryService,
  type TemplateAdoption,
  type TemplateUpdatePreview,
} from '@/services/email-template-library.service';

/**
 * Platform Update Window — Phase 12.5C.
 *
 * `keep-current` is deliberately NOT offered. The backend route changes nothing
 * and leaves the update badge in place, so beside "Ignore this version" it would
 * read as a second dismiss button that silently fails to dismiss. The design
 * permits omitting it rather than shipping two controls that look equivalent and
 * behave differently. The route remains for API compatibility.
 *
 * `releasedBy` is also not rendered: platform-user name resolution is deferred to
 * Phase D, so the field currently carries a UUID.
 */
export function UpdateWindow({
  template,
  canEdit,
  onChanged,
}: {
  template: TemplateAdoption;
  canEdit: boolean;
  onChanged: (next: TemplateAdoption) => void;
}) {
  const [data, setData] = useState<TemplateUpdatePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    emailTemplateLibraryService
      .updatePreview(template.templateKey)
      .then(setData)
      .catch((e: Error) => setError(e.message || 'The update details could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [template.templateKey]);

  const run = async (fn: () => Promise<TemplateAdoption>, msg: string) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await fn());
      toast.success(msg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The action could not be completed.');
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async () => {
    const ok = await swalConfirm({
      title: 'Adopt the platform version?',
      html:
        '<p style="margin:0 0 8px">Your customization will be replaced by the current platform ' +
        'version. <strong>This changes what your workspace sends.</strong></p>' +
        '<p style="margin:0">Your previous version is kept in the template history.</p>',
      confirmText: 'Update now',
    });
    if (!ok) return;
    await run(
      () => emailTemplateLibraryService.updateFromPlatform(template.templateKey),
      'Updated to the platform version',
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const notes = data?.releaseNotes ?? null;

  return (
    <div className="space-y-3">
      {error && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
        <span className="text-muted-foreground">
          You are sending{' '}
          <span className="font-mono text-foreground">
            v{data?.currentVersion ?? template.tenantVersion ?? '—'}
          </span>
        </span>
        <span aria-hidden className="text-muted-foreground">
          →
        </span>
        <span className="text-muted-foreground">
          Platform{' '}
          <span className="font-mono text-foreground">
            v{data?.platformVersion ?? template.platformVersion ?? '—'}
          </span>
        </span>
        {data?.releaseDate && (
          <span className="ml-auto text-xs text-muted-foreground">
            Released {new Date(data.releaseDate).toLocaleDateString()}
          </span>
        )}
      </div>

      {data?.breakingChanges && data.breakingChanges !== 'None' && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Breaking changes
          </p>
          <p className="mt-0.5 text-sm text-foreground">{data.breakingChanges}</p>
        </div>
      )}

      {notes?.summary ? (
        <div className="rounded-lg border border-border p-3">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            What changed
          </p>
          <p className="text-sm text-foreground">{notes.summary}</p>
          {(notes.featuresAdded?.length ?? 0) > 0 && (
            <ul className="ml-4 mt-2 list-disc text-xs text-foreground">
              {notes.featuresAdded!.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Your service provider did not supply release notes for this version.
        </p>
      )}

      {(data?.variables.added.length || data?.variables.removed.length) && (
        <div className="rounded-lg border border-border p-3">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Variables
          </p>
          {data!.variables.added.length > 0 && (
            <p className="font-mono text-xs text-primary">+ {data!.variables.added.join(', ')}</p>
          )}
          {data!.variables.removed.length > 0 && (
            <p className="font-mono text-xs text-destructive">
              − {data!.variables.removed.join(', ')}
            </p>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex flex-col gap-2 pt-1 sm:flex-row">
          <Button
            className="min-h-[44px] sm:min-h-0"
            disabled={busy || !template.updateAvailable}
            onClick={() => void handleUpdate()}
          >
            Update now
          </Button>
          {template.updateAvailable && (
            <Button
              variant="secondary"
              className="min-h-[44px] sm:min-h-0"
              disabled={busy}
              onClick={() =>
                void run(
                  () => emailTemplateLibraryService.ignoreVersion(template.templateKey),
                  'Update ignored — you will be notified on the next release',
                )
              }
            >
              Ignore this version
            </Button>
          )}
        </div>
      )}

      {template.adoptionStatus === 'IGNORED' && (
        <p className="text-xs text-muted-foreground">
          You dismissed this version. Your customized template keeps sending, and you will be
          notified again when your service provider publishes the next release.
        </p>
      )}
    </div>
  );
}
